<template>
  <div
    class="h-fit cursor-pointer rounded-5px hover:shadow-dropdown"
    :class="{
      'w-240px': layoutStore.isDesktop,
      'w-180px': layoutStore.isMobile,
    }"
    @click="
      $router.push({
        name: redirects[content.artworkType],
        params: {
          id: content.displayId,
        },
      })
    "
  >
    <div class="relative w-full bg-light-gray">
      <MultiLayerPreview
        v-if="content.preview.type === 'multi-layer'"
        :data="content.preview"
        :style="{
          width: `${previewAreaSize}px`,
          height: `${previewAreaSize}px`,
        }"
      />
      <div
        v-else
        class="flex flex-wrap items-center justify-center overflow-hidden"
        :style="{
          width: `${previewAreaSize}px`,
          height: `${previewAreaSize}px`,
          padding: `${previewAreaPadding}px`,
        }"
      >
        <img
          v-if="content.preview.type === 'single-image'"
          alt="image"
          class="max-h-full max-w-full rounded-10px"
          :src="content.preview.imageUrl"
        />
        <img
          v-for="(url, index) in content.preview.imageUrls.slice(0, 4)"
          v-else-if="content.preview.type === 'multi-images'"
          :key="index"
          alt="image"
          class="h-50% w-50%"
          :src="url"
        />
      </div>
      <p
        v-if="
          content.customizationLevel === 'NoMixedArtistWork' ||
          content.customizationLevel === 'NoCustomization'
        "
        class="absolute left-0px top-0px m-2px flex items-center gap-2px content-10-h12-r text-primary-40"
      >
        <i
          class="text-16px"
          :class="{
            'icon-edit-limited': content.customizationLevel === 'NoMixedArtistWork',
            'icon-edit-prohibit': content.customizationLevel === 'NoCustomization',
          }"
        />
        {{
          content.customizationLevel === 'NoCustomization'
            ? $t('customization_level.no_customization')
            : $t('customization_level.no_mixed_artistWork')
        }}
      </p>
      <i
        class="absolute top-2px text-24px"
        :class="{
          'icon-heart text-primary-60': !isFavorite,
          'icon-heart-fill text-brand-blushPink': isFavorite,
          'right-4px': layoutStore.isDesktop,
          'right-2px': layoutStore.isMobile,
        }"
        @click.stop="
          async () => {
            pushGA4ButtonClick(`${isFavorite ? 'unfavorite' : 'favorite'} ${content.artworkType}`)
            changeFavoriteArtwork()
            await favoriteArtwork(isFavorite)
          }
        "
        @mouseleave="mouseleaveEvent"
        @mouseover="mouseoverEvent"
      />
    </div>
    <div
      class="h-80px flex flex-col justify-between rounded-b-5px bg-white py-12px"
      :class="{
        'px-16px': layoutStore.isDesktop,
        'px-12px': layoutStore.isMobile,
      }"
    >
      <p class="w-full truncate text-center content-11-h16-r text-primary-100">
        {{ content.title }}
      </p>
      <p class="w-full truncate text-center content-11-h16-r text-primary-40">
        {{ content.subTitle === null ? '' : content.subTitle }}
      </p>
      <div class="w-full flex justify-center gap-8px">
        <p class="truncate content-12-h16-b text-brand-deep-yellow">
          {{ priceWithLocaleCurrencyUnit(content.price) }}
        </p>
        <p
          v-if="content.compareAtPrice !== content.price"
          class="truncate content-11-h16-r text-primary-40 line-through"
        >
          {{ priceWithLocaleCurrencyUnit(content.compareAtPrice) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from 'villus'
import { computed, onMounted, ref } from 'vue'

import {
  ArtworkType,
  FavoriteBackgroundDocument,
  FavoriteCollageDocument,
  FavoriteStickerGroupDocument,
} from '@/graphql/creator-platform/generated'
import { priceWithLocaleCurrencyUnit } from '@/locale'
import { Route } from '@/router/route'
import { useLayoutStore } from '@/store/layoutStore'
import {
  addFavoriteArtwork,
  isFavoriteArtwork,
  removeFavoriteArtwork,
} from '@/utils/favoriteArtwork'
import { pushGA4ButtonClick } from '@/utils/googleAnalytics'

import { type DisplayCardContent } from './DisplayCard'
import MultiLayerPreview from './MultiLayerPreview.vue'

const props = defineProps<{ content: DisplayCardContent }>()

const layoutStore = useLayoutStore()

const isFavorite = ref(false)
function checkFavoriteArtwork(): void {
  isFavorite.value = isFavoriteArtwork(props.content)
}

const redirects: Record<ArtworkType, Route> = {
  [ArtworkType.Background]: Route.BackgroundPage,
  [ArtworkType.Collage]: Route.CollagePage,
  [ArtworkType.StickerGroup]: Route.StickerGroupPage,
}

const previewAreaSize = computed(() => (layoutStore.isDesktop ? 240 : 180))
const previewAreaPadding = computed(() => (layoutStore.isDesktop ? 20 : 15))

onMounted(() => {
  checkFavoriteArtwork()
})

function changeFavoriteArtwork(): void {
  if (isFavorite.value) {
    removeFavoriteArtwork(props.content.id)
  } else {
    addFavoriteArtwork(props.content)
  }
  checkFavoriteArtwork()
}

function mouseoverEvent(event: MouseEvent): void {
  if (!(event.target instanceof HTMLElement) || isFavorite.value) {
    return
  }
  event.target.classList.add('icon-heart-fill')
  event.target.classList.remove('icon-heart')
}

function mouseleaveEvent(event: MouseEvent): void {
  if (!(event.target instanceof HTMLElement) || isFavorite.value) {
    return
  }
  event.target.classList.add('icon-heart')
  event.target.classList.remove('icon-heart-fill')
}

async function favoriteArtwork(favoriteStatus: boolean): Promise<void> {
  const query =
    props.content.artworkType === ArtworkType.StickerGroup
      ? FavoriteStickerGroupDocument
      : props.content.artworkType === ArtworkType.Background
        ? FavoriteBackgroundDocument
        : FavoriteCollageDocument
  await useQuery({
    query,
    variables: {
      id: props.content.id,
      favoriteStatus,
    },
  })
}
</script>
